// Budget requests: a member asks, an admin grants dollars onto the org's
// monthly alert limit (org.costLimits). Pure — the admin UI and the tests
// share applyGrant so a second click cannot invent a different formula.
// Granting does not expire, pause work, or enable GPU / heavy compute.

// 'credits' asks for prepaid model credits: the admin fulfils it by buying
// credits (shared/models/credits.js), not by moving the alert line.
export const PURPOSES = ['upgrade', 'workflow', 'job', 'credits', 'other'];

export const PURPOSE_LABELS = {
  upgrade: 'Upgrade',
  workflow: 'Workflow',
  job: 'Job',
  credits: 'Model credits',
  other: 'Other',
};

export const RESOURCES = ['workers', 'gpu', 'cloud', 'models'];

export const RESOURCE_LABELS = {
  workers: 'Workers',
  gpu: 'GPU',
  cloud: 'Other cloud services',
  models: 'Model usage',
};

export const MAX_USD = 1_000_000;

export function roundUsd(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return NaN;
  return Math.round(x * 100) / 100;
}

function validUsd(n) {
  return Number.isFinite(n) && n > 0 && n <= MAX_USD;
}

// Adds grantedUsd onto the app's monthly limit, or the org monthly limit
// when there is no app. Does not mutate the input.
export function applyGrant(costLimits, { appId, grantedUsd } = {}) {
  const add = roundUsd(grantedUsd);
  if (!validUsd(add)) throw new Error('Enter an amount in USD.');
  const limits = costLimits && typeof costLimits === 'object' ? { ...costLimits } : {};
  const id = String(appId || '').trim();
  if (id) {
    const apps = { ...(limits.apps && typeof limits.apps === 'object' ? limits.apps : {}) };
    apps[id] = roundUsd((Number(apps[id]) || 0) + add);
    return { ...limits, apps };
  }
  return { ...limits, monthlyUsd: roundUsd((Number(limits.monthlyUsd) || 0) + add) };
}

// What the form may file. Returns { error } or { value }.
export function cleanRequest(input = {}) {
  const purpose = PURPOSES.includes(input.purpose) ? input.purpose : '';
  const amountUsd = roundUsd(input.amountUsd);
  const resources = [...new Set((Array.isArray(input.resources) ? input.resources : []).filter((r) => RESOURCES.includes(r)))];
  const note = String(input.note || '').trim().slice(0, 2000);
  const appId = String(input.appId || '').trim();
  const pipelineId = (purpose === 'workflow' || purpose === 'job') ? String(input.pipelineId || '').trim() : '';
  if (!purpose) return { error: 'Pick what this budget is for.' };
  if (!validUsd(amountUsd)) return { error: 'Enter an amount in USD.' };
  return { value: { purpose, amountUsd, resources, note, appId, pipelineId } };
}
